import {
  useLazyQuery,
  OperationVariables,
  useReactiveVar,
} from '@apollo/client';
import { ProgressIndicator, Stack, Text } from '@fluentui/react';
import {
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  Controls,
  Edge,
  Node,
  NodeTypes,
  ReactFlow,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import clsx from 'clsx';
import {
  buildHierarchyTree,
  getLayoutedElements,
} from 'common/components/PreviewApprovalHierarchyReactFlow/utils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DocumentNode } from 'graphql';
import { showCustomToast } from '../CustomToast/utils';
import { useCommonStyles } from 'common/styles';
import { useStyles } from './index.styles';
import { DEFAULT_NODE_HEIGHT } from './constants';
import {
  CustomReactFlowControls,
  setInteractive,
} from 'common/components/CustomReactFlowControls';

export interface HierarchyNode {
  id?: string | null;
  children?: HierarchyNode[];
  level?: number | null;
  width?: number;
  height?: number;
  [key: string]: unknown; // Allow additional properties for flexibility
}

interface GenericHierarchyFlowProps<
  TData,
  TVariables extends OperationVariables
> {
  entityId: string;
  defaultNodeHeight?: number;
  height?: number;
  width?: number;
  query: DocumentNode;
  queryVariables?: TVariables;
  getNodesFromData: (data: TData) => HierarchyNode[] | undefined;
  onNodeData?: (nodeData: HierarchyNode | undefined) => void;
  NodeViewComponent: React.ComponentType<{ data: HierarchyNode; key: string }>;
}

const LOADING_TEXT = 'Loading hierarchy';

const LoadingHierarchy = () => {
  const commonStyles = useCommonStyles();
  const styles = useStyles();
  return (
    <Stack
      horizontalAlign="center"
      verticalAlign="center"
      className={styles.loaderContainer}
    >
      <Stack>
        <Text className={clsx(commonStyles.semibold, styles.loadingText)}>
          {LOADING_TEXT}
        </Text>
        <ProgressIndicator />
      </Stack>
    </Stack>
  );
};

export const GenericHierarchyFlow = <
  TData,
  TVariables extends OperationVariables
>({
  entityId,
  height,
  defaultNodeHeight,
  width,
  query,
  queryVariables,
  getNodesFromData,
  onNodeData,
  NodeViewComponent,
}: GenericHierarchyFlowProps<TData, TVariables>) => {
  type FlowNodeData = HierarchyNode & { [key: string]: unknown };

  const [rfNodes, setRfNodes] = useState<Node<FlowNodeData>[]>([]);
  const [rfEdges, setRfEdges] = useState<Edge[]>([]);
  const isInteractive = useReactiveVar(setInteractive);

  const onNodesChange = useCallback(
    (changes: any) => setRfNodes((nds) => applyNodeChanges(changes, nds)),
    []
  );

  const onEdgesChange = useCallback(
    (changes: any) => setRfEdges((eds) => applyEdgeChanges(changes, eds)),
    []
  );

  const [getHierarchyData, { loading, data, error }] = useLazyQuery<
    TData,
    TVariables
  >(query, {
    fetchPolicy: 'network-only',
    variables: queryVariables,
  });
  useEffect(() => {
    if (entityId) {
      getHierarchyData();
    }
  }, [entityId, getHierarchyData]);

  useEffect(() => {
    if (!loading && data) {
      const raw = getNodesFromData(data);
      if (raw?.length) {
        const nodesWithChildren = raw.map((item) => ({
          ...item,
          children: [],
          level: item.level ?? 0,
          width: 300,
          height: 200,
        }));

        const roots = buildHierarchyTree(nodesWithChildren);
        const newNodes: Node<FlowNodeData>[] = [];
        const newEdges: Edge[] = [];

        const flatten = (node: HierarchyNode) => {
          if (!node.id) return;

          newNodes.push({
            id: node.id,
            data: node as FlowNodeData,
            type: 'hierarchyNode',
            position: { x: 0, y: 0 },
            style: {
              zIndex: 1,
              pointerEvents: 'all',
            },
          });

          if (node.children && node.children.length > 0) {
            node.children.forEach((child) => {
              if (child.id && node.id) {
                newEdges.push({
                  id: `${node.id}-${child.id}`,
                  source: node.id,
                  target: child.id,
                  type: 'step',
                  animated: true,
                });
                flatten(child);
              }
            });
          }
        };

        roots.forEach(flatten);

        const { nodes: layouted, edges: layoutedEdges } = getLayoutedElements(
          newNodes,
          newEdges,
          {
            direction: 'TB',
            defaultNodeHeight: defaultNodeHeight
              ? defaultNodeHeight
              : DEFAULT_NODE_HEIGHT,
          }
        );
        setRfNodes(layouted);
        setRfEdges(layoutedEdges);

        if (onNodeData && raw[0]) {
          onNodeData(raw[0]);
        }
      }
    }
  }, [data, loading, getNodesFromData, onNodeData]);

  const nodeTypes: NodeTypes = useMemo(
    () => ({
      hierarchyNode: (props) => (
        <NodeViewComponent data={props.data} key={props.id} />
      ),
    }),
    [NodeViewComponent]
  );

  if (loading) return <LoadingHierarchy />;
  if (error) {
    showCustomToast({
      content: 'Could not load data',
      appearance: 'error',
    });
    return (
      <Stack
        horizontalAlign="center"
        verticalAlign="center"
        style={{ height: 600 }}
      >
        <Stack style={{ width: 600 }}>
          <Text variant="xLarge">{error.message}</Text>
        </Stack>
      </Stack>
    );
  }

  return (
    <ReactFlowProvider>
      <Stack style={{ flex: 1 }}>
        <div
          style={{
            height: height ? height : 600,
            width: width ? width : '100%',
          }}
        >
          <ReactFlow
            nodes={rfNodes}
            edges={rfEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            proOptions={{ hideAttribution: true }}
            panOnScroll
            minZoom={0.1}
            panOnDrag={isInteractive}
            nodesDraggable={isInteractive}
            nodesConnectable={isInteractive}
            elementsSelectable={isInteractive}
            zoomOnScroll={isInteractive}
            zoomOnPinch={isInteractive}
            zoomOnDoubleClick={isInteractive}
            onInit={(flow) => {
              const nodes = flow.getNodes();
              if (!nodes.length) return;
              const minX = Math.min(...nodes.map((n) => n.position.x));
              const maxX = Math.max(
                ...nodes.map((n) => n.position.x + (n.width ?? 0))
              );
              const centerX = (minX + maxX) / 2;
              const zoom = 0.5;
              const containerWidth = width ? width : 1200;
              const x = containerWidth / 2 - centerX * zoom;
              flow.setViewport({
                x: x < 300 ? 300 : x,
                y: 20,
                zoom,
              });
            }}
          >
            <Background />
            <CustomReactFlowControls />
          </ReactFlow>
        </div>
      </Stack>
    </ReactFlowProvider>
  );
};
